import { IconButton, Slide } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { memo, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { A11y, Navigation, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { visualBuilderDataSelector } from "@/store/selectors";

import FullscreenThumbnail from "./FullscreenThumbnail";
import { CONTENT_TYPE, TAB_STATUSES } from "../../../constants";
import { useUrlParams } from "../../../hooks";
import { ArrowDownIcon, ArrowLeftIcon, ArrowRightIcon, ArrowUpIcons, IconPlay, IconStop } from "../../../icons";
import { updateVisualBuilderData } from "../../../store/actions";
import useWindowSize from "../../../utils/useWindowSize";

import "swiper/css";
import "swiper/css/navigation"; // Navigation module
import "swiper/css/virtual";

const useStyles = makeStyles({
  homeBannerCarousel: {
    position: "fixed !important",
    bottom: "0px",
    width: "100%",
    height: "calc(8rem + 20px)",
    pointerEvents: "none",
    "& *": {
      pointerEvents: "auto",
    },
    "@media (max-width:767px)": {
      paddingLeft: "0px",
      paddingRight: "0px",
    },
  },
  thumbnailButtons: {
    "backdrop-filter": "blur(5px)",
    left: (props) => (props.leftDrawer ? "240px" : "0px"),
    marginLeft: "10px",
    bottom: "148px",
    position: "fixed",
    backgroundColor: "rgba(0,0,0,0.7) !important",
    color: "white",
    padding: "8px",
    borderRadius: 40,
  },
  carousel: {
    padding: "4px",
    transition: "all 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
  },
  thumbnailsPlay: {
    padding: "4px",
    marginLeft: "10px",
    transition: "all 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
  },
  iconplay: {
    fontSize: "1.6rem",
    stroke: "#FFF",
    fill: "none",
  },
  swiperContainer: {
    width: "100%",
    height: "calc(8rem + 20px)",
    position: "fixed !important",
    zIndex: "0 !important",
    bottom: "0px",
    transition: "all 225ms cubic-bezier(0, 0, 0.2, 1) 0ms !important",
    paddingLeft: (props) => (props.leftDrawer ? "240px" : "0"),
    paddingRight: (props) => (props.rightDrawer ? "240px" : "0"),
  },
  thumbnailsSwiperContainer: {
    width: "100%",
    margin: "0 !important",
    background: "rgba(216, 216, 216, 0.1)",
    "backdrop-filter": "blur(5px)",
    padding: "10px !important",
    height: "calc(8rem + 20px) !important",
    "& > div": {
      cursor: ({ cursorMode }) => (cursorMode ? "inherit" : "grab"),
    },
  },
  swiperSlide: {
    width: "8rem !important",
    height: "8rem !important",
    marginRight: "0px !important",
    paddingRight: "10px !important",
  },
  slideButton: {
    bottom: 0,
    zIndex: 2,
    position: "absolute",
    transition: "all 225ms cubic-bezier(0, 0, 0.2, 1) 0ms",
    backgroundColor: "rgba(0,0,0,.5)",
    height: "100%",
    borderRadius: 0,
    "&:hover": {
      backgroundColor: "rgba(0,0,0,.8)",
    },
    "&.swiper-button-disabled": {
      display: "none",
    },
  },
  previousSlide: { right: "0px" },
  nextSlide: { left: "0px" },
  unembed: {
    width: "0rem !important",
    height: "8rem !important",
  },
});

const ContentMemo = memo((props) => {
  const { mediaItem, index, isActive, cursorMode } = props;
  return <FullscreenThumbnail mediaItem={mediaItem} cursorMode={cursorMode} index={index} isActive={isActive} />;
});

export default function ThumbnailsCarousel(props) {
  const {
    branding,
    leftDrawerAutoHide,
    rightDrawerAutoHide,
    leftDrawer,
    rightDrawer,
    thumbnailsPlay,
    setThumbnailsPlay,
    setThumbsSwiper,
    thumbsSwiper,
    style,
    tabStatus,
    filteredMediaItems = [],
    currentMediaItemId,
    carousel = false,
  } = props;

  const { hideCarousel } = branding.settings || {};

  const params = useUrlParams();
  const dispatch = useDispatch();
  const [showCarouselToggle, setShowCarouselToggle] = useState(!hideCarousel);
  const {
    visualBuilderData: { locked },
  } = useSelector(visualBuilderDataSelector(params));

  useEffect(() => {
    setShowCarouselToggle(filteredMediaItems.length > 1 && hideCarousel);
  }, [hideCarousel, filteredMediaItems.length]);

  const leftDrawerWithHide = leftDrawer && !leftDrawerAutoHide;
  const rightDrawerWithHide = rightDrawer && !rightDrawerAutoHide;

  const cursorMode =
    (filteredMediaItems.length * 138) /
      (useWindowSize()[0] - ((leftDrawerWithHide ? 240 : 0) + (rightDrawerWithHide ? 240 : 0))) <
    1;

  const classes = useStyles({
    ...props,
    leftDrawer: leftDrawerWithHide,
    rightDrawer: rightDrawerWithHide,
    cursorMode,
  });

  const swiperRef = useRef(null);

  const handleThumbnails = () => {
    if (locked && tabStatus === TAB_STATUSES.CUSTOMIZE) return;

    dispatch(updateVisualBuilderData(params, { carousel: !carousel }));
  };

  return showCarouselToggle ? (
    <div data-testid="thumbnails-carousel" className={classes.homeBannerCarousel} style={style}>
      <div
        data-testid="thumbnails-buttons"
        className={classes.thumbnailButtons}
        style={{ bottom: carousel ? "160px" : "10px" }}
      >
        <IconButton
          aria-label="toggle carousel expand/collapse"
          className={classes.carousel}
          size="small"
          onClick={handleThumbnails}
          color="secondary"
        >
          {carousel ? <ArrowDownIcon htmlColor="#fff" /> : <ArrowUpIcons htmlColor="#fff" />}
        </IconButton>
        {filteredMediaItems.length > 1 ? (
          <IconButton
            data-testid="thumbnails-play-button"
            aria-label="thumbnails play"
            className={classes.thumbnailsPlay}
            size="small"
            color="secondary"
            onClick={() => {
              if (locked && tabStatus === TAB_STATUSES.CUSTOMIZE) return;

              setThumbnailsPlay(!thumbnailsPlay);
            }}
          >
            {thumbnailsPlay ? (
              <IconStop htmlColor="#fff" className={classes.iconplay} />
            ) : (
              <IconPlay htmlColor="#fff" className={classes.iconplay} />
            )}
          </IconButton>
        ) : null}
      </div>
      <Slide direction="up" in={carousel}>
        <div className={classes.swiperContainer}>
          <Swiper
            data-testid="thumbnails-carousel-swiper"
            modules={[A11y, Navigation, Virtual]}
            ref={swiperRef}
            slidesPerView={isMobile ? 8 : 16}
            onSwiper={setThumbsSwiper}
            spaceBetween={0}
            freeMode
            className={classes.thumbnailsSwiperContainer}
            onSlideChange={(e) => {
              if (!e.touchEventsData.isTouched) {
                thumbsSwiper?.update();
              }
            }}
            lazy={{
              preloadImages: false,
              loadPrevNext: true,
              loadPrevNextAmount: 20,
            }}
            navigation={{
              nextEl: ".swiper-next",
              prevEl: ".swiper-prev",
            }}
            virtual
          >
            {filteredMediaItems.map((mediaItem, index) => {
              const { xFrameOptions, contentType, link } = mediaItem;
              const isActive = mediaItem.id === currentMediaItemId;

              return (
                <SwiperSlide
                  data-testid="thumbnails-carousel-swiper-slide"
                  key={`thumbnail-${mediaItem.id}`}
                  className={classes.swiperSlide}
                  virtualIndex={index}
                  onClick={() => {
                    if (
                      xFrameOptions &&
                      xFrameOptions !== "NONE" &&
                      contentType.value === CONTENT_TYPE.LINK &&
                      window.confirm("Open link in new tab?")
                    ) {
                      window.open(link, "_blank");
                    }
                  }}
                >
                  <ContentMemo cursorMode={cursorMode} mediaItem={mediaItem} index={index} isActive={isActive} />
                </SwiperSlide>
              );
            })}
            <IconButton
              aria-label="Go to next slide"
              className={`${classes.slideButton} ${classes.previousSlide} swiper-next`}
              size="large"
            >
              <ArrowRightIcon fontSize="small" htmlColor="#fff" />
            </IconButton>
            <IconButton
              aria-label="Go to previous slide"
              className={`${classes.slideButton} ${classes.nextSlide} swiper-prev`}
              size="large"
            >
              <ArrowLeftIcon fontSize="small" htmlColor="#fff" />
            </IconButton>
          </Swiper>
        </div>
      </Slide>
    </div>
  ) : null;
}
